import {
  InventoryTagStatus,
  TagType,
  QuantityCaptureMethod,
  Unit,
} from '@rematter/frontend-lib-api-client';

import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';

export type TagAllocationInput = {
  movementId: string;
  quantity: {
    value: number;
    unit: Unit;
  };
};

export const createInventoryTagAtom = createMutationAtomFactory(
  'inventory.createInventoryTag',
  (params: {
    tagNumber: string;
    tagType: TagType;
    generatedAt: string;
    status: InventoryTagStatus;
    grossQuantity: number;
    tare: number;
    itemId: string;
    facilityId: string;
    lotId?: string;
    isRevised: boolean;
    quantityCaptureMethod: QuantityCaptureMethod;
    tareCaptureMethod: QuantityCaptureMethod;
  }) => ({
    inventoryInventoryTagCreate: {
      __args: {
        input: {
          tagNumber: params.tagNumber,
          tagType: params.tagType,
          generatedAt: params.generatedAt,
          status: params.status,
          grossQuantity: params.grossQuantity,
          tare: params.tare,
          itemId: params.itemId,
          facilityId: params.facilityId,
          lotId: params.lotId,
          isRevised: params.isRevised,
          quantityCaptureMethod: params.quantityCaptureMethod,
          tareCaptureMethod: params.tareCaptureMethod,
        },
      },
      id: true,
      tagNumber: true,
      tagType: true,
      generatedAt: true,
      status: true,
      grossQuantity: true,
      tare: true,
      netQuantity: true,
      item: {
        id: true,
        name: true,
        skuNumber: true,
      },
      facilityId: true,
      lotId: true,
      lot: {
        id: true,
        lotNumber: true,
      },
      isRevised: true,
      quantityCaptureMethod: true,
      tareCaptureMethod: true,
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
);

export const updateInventoryTagAtom = createMutationAtomFactory(
  'inventory.updateInventoryTag',
  (params: {
    id: string;
    tagNumber?: string;
    tagType?: TagType;
    generatedAt?: string;
    status?: InventoryTagStatus;
    grossQuantity?: number;
    tare?: number;
    itemId?: string;
    lotId?: string | null;
    isRevised?: boolean;
    quantityCaptureMethod?: QuantityCaptureMethod;
    tareCaptureMethod?: QuantityCaptureMethod;
  }) => ({
    inventoryInventoryTagUpdate: {
      __args: {
        id: params.id,
        input: {
          tagNumber: params.tagNumber,
          tagType: params.tagType,
          generatedAt: params.generatedAt,
          status: params.status,
          grossQuantity: params.grossQuantity,
          tare: params.tare,
          itemId: params.itemId,
          lotId: params.lotId,
          isRevised: params.isRevised,
          quantityCaptureMethod: params.quantityCaptureMethod,
          tareCaptureMethod: params.tareCaptureMethod,
        },
      },
      id: true,
      tagNumber: true,
      tagType: true,
      generatedAt: true,
      status: true,
      grossQuantity: true,
      tare: true,
      netQuantity: true,
      item: {
        id: true,
        name: true,
        skuNumber: true,
      },
      facilityId: true,
      lotId: true,
      lot: {
        id: true,
        lotNumber: true,
      },
      isRevised: true,
      quantityCaptureMethod: true,
      tareCaptureMethod: true,
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
);

export const deleteInventoryTagAtom = createMutationAtomFactory(
  'inventory.deleteInventoryTag',
  (params: { id: string }) => ({
    inventoryInventoryTagDelete: {
      __args: {
        id: params.id,
      },
    },
  }),
);

export const allocateToTagAtom = createMutationAtomFactory(
  'inventory.allocateToTag',
  (params: { tagId: string; allocations: TagAllocationInput[] }) => ({
    inventoryTagAllocate: {
      __args: {
        input: {
          tagId: params.tagId,
          allocations: params.allocations.map((alloc) => ({
            movementId: alloc.movementId,
            quantity: {
              value: alloc.quantity.value,
              unit: alloc.quantity.unit,
            },
          })),
        },
      },
      tag: {
        id: true,
        tagNumber: true,
        status: true,
      },
      allocations: {
        id: true,
        movementId: true,
        quantity: {
          value: true,
          unit: true,
        },
        timestamps: {
          createdAt: true,
        },
      },
    },
  }),
);

export const deallocateFromTagAtom = createMutationAtomFactory(
  'inventory.deallocateFromTag',
  (params: { allocationIds: string[] }) => ({
    inventoryTagDeallocate: {
      __args: {
        input: {
          allocationIds: params.allocationIds,
        },
      },
    },
  }),
);
